import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';

import { ApiService } from '../../core/services/api.service';

interface TaskRow {
  id: string;
  title: string;
  description?: string;
  status: number;
  statusLabel?: string;
  assigneeId?: string;
  teamLeadId?: string;
  teamLeadName?: string;
  dueDate?: string;
  isOverdue?: boolean;
  delayLabel?: string;
  isDistributed?: boolean;
  allowedTransitions?: { status: number; label: string }[];
}

interface MemberOption {
  employeeId: string;
  name: string;
  jobTitle?: string;
}

interface LeadOption {
  leadEmployeeId: string;
  leadName: string;
  teamName: string;
}

interface DashboardData {
  cards: {
    totalAssigned: number;
    pendingDistribution: number;
    inProgress: number;
    awaitingReview: number;
    completed: number;
    overdue: number;
  };
  myAssignedTasks: TaskRow[];
  teamTasks: TaskRow[];
  pendingDistribution: TaskRow[];
  inProgress: TaskRow[];
  awaitingReview: TaskRow[];
  completed: TaskRow[];
  overdue: TaskRow[];
  scopeNote?: string;
  isPrivilegedView?: boolean;
  filterTeamLeadId?: string | null;
  viewerEmployeeId?: string | null;
}

@Component({
  selector: 'app-team-lead-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTabsModule
  ],
  template: `
<div class="page">
  <header>
    <div>
      <h1>Team Lead Dashboard</h1>
      <p>Receive tasks assigned to you, distribute them to your team, and review progress.</p>
    </div>
    <div class="header-actions">
      @if (isPrivileged()) {
        <mat-form-field appearance="outline" class="lead-filter">
          <mat-label>Team lead filter</mat-label>
          <mat-select [ngModel]="selectedLeadId()" (ngModelChange)="onLeadFilterChange($event)">
            <mat-option value="">All team leads</mat-option>
            @for (l of leads(); track l.leadEmployeeId) {
              <mat-option [value]="l.leadEmployeeId">{{ l.leadName }} · {{ l.teamName }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
      <button mat-stroked-button type="button" (click)="load()">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </header>

  @if (scopeNote()) {
    <div class="scope-note">
      <mat-icon>info</mat-icon>
      <span>{{ scopeNote() }}</span>
    </div>
  }

  @if (error()) {
    <div class="error">{{ error() }}</div>
  }
  @if (loading()) {
    <mat-spinner diameter="36"></mat-spinner>
  }

  @if (data(); as d) {
    <div class="cards">
      <mat-card><strong>{{ d.cards.totalAssigned }}</strong><span>Total assigned</span></mat-card>
      <mat-card><strong>{{ d.cards.pendingDistribution }}</strong><span>Pending distribution</span></mat-card>
      <mat-card><strong>{{ d.cards.inProgress }}</strong><span>In progress</span></mat-card>
      <mat-card><strong>{{ d.cards.awaitingReview }}</strong><span>Awaiting review</span></mat-card>
      <mat-card><strong>{{ d.cards.completed }}</strong><span>Completed</span></mat-card>
      <mat-card class="warn"><strong>{{ d.cards.overdue }}</strong><span>Overdue</span></mat-card>
    </div>

    <mat-tab-group>
      <mat-tab label="Pending distribution">
        @for (t of d.pendingDistribution; track t.id) {
          <mat-card class="task">
            <h3>{{ t.title }}</h3>
            <p>{{ t.description || 'No description' }}</p>
            <p class="meta">{{ t.statusLabel || t.status }}</p>
            @if (t.teamLeadName) {
              <p class="meta"><mat-icon class="inline">person</mat-icon> Team lead: {{ t.teamLeadName }}</p>
            }
            <div class="row">
              <mat-form-field appearance="outline">
                <mat-label>Assign to team member</mat-label>
                <mat-select [(ngModel)]="assignMap[t.id]">
                  @for (m of membersFor(t.id); track m.employeeId) {
                    <mat-option [value]="m.employeeId">{{ m.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-flat-button color="primary" type="button" (click)="distribute(t)" [disabled]="!assignMap[t.id]">
                Distribute
              </button>
              <button mat-stroked-button type="button" (click)="loadMembers(t.id)">Load members</button>
            </div>
          </mat-card>
        } @empty {
          <p class="empty">No tasks waiting for distribution. Tasks show here only for the selected team lead (or all leads if you are an admin).</p>
        }
      </mat-tab>
      <mat-tab label="My assigned">
        @for (t of d.myAssignedTasks; track t.id) {
          <mat-card class="task">
            <h3>{{ t.title }}</h3>
            <p class="meta">{{ t.statusLabel }}</p>
            @if (t.teamLeadName) {
              <p class="meta">Lead: {{ t.teamLeadName }}</p>
            }
          </mat-card>
        } @empty { <p class="empty">None</p> }
      </mat-tab>
      <mat-tab label="Team tasks">
        @for (t of d.teamTasks; track t.id) {
          <mat-card class="task"><h3>{{ t.title }}</h3><p class="meta">{{ t.statusLabel }}</p></mat-card>
        } @empty { <p class="empty">None</p> }
      </mat-tab>
      <mat-tab label="In progress">
        @for (t of d.inProgress; track t.id) {
          <mat-card class="task"><h3>{{ t.title }}</h3><p class="meta">{{ t.statusLabel }}</p></mat-card>
        } @empty { <p class="empty">None</p> }
      </mat-tab>
      <mat-tab label="Awaiting review">
        @for (t of d.awaitingReview; track t.id) {
          <mat-card class="task">
            <h3>{{ t.title }}</h3>
            <div class="actions">
              @for (tr of t.allowedTransitions || []; track tr.status) {
                <button mat-stroked-button type="button" (click)="transition(t, tr.status)">{{ tr.label }}</button>
              }
            </div>
          </mat-card>
        } @empty { <p class="empty">None</p> }
      </mat-tab>
      <mat-tab label="Completed">
        @for (t of d.completed; track t.id) {
          <mat-card class="task"><h3>{{ t.title }}</h3></mat-card>
        } @empty { <p class="empty">None</p> }
      </mat-tab>
      <mat-tab>
        <ng-template mat-tab-label>
          Overdue <span class="tab-badge warn">{{ d.overdue.length }}</span>
        </ng-template>
        @for (t of d.overdue; track t.id) {
          <mat-card class="task overdue">
            <h3>{{ t.title }}</h3>
            <p class="meta">{{ t.statusLabel || t.status }}</p>
            @if (t.dueDate) {
              <p class="due overdue"><mat-icon>event</mat-icon> {{ t.dueDate }}{{ t.isOverdue ? ' · Overdue' : '' }}</p>
            }
            @if (t.delayLabel) { <p class="meta">{{ t.delayLabel }}</p> }
          </mat-card>
        } @empty { <p class="empty">No overdue tasks.</p> }
      </mat-tab>
    </mat-tab-group>
  }
</div>
  `,
  styles: [
    `
      .page { max-width: 1100px; }
      header { display: flex; justify-content: space-between; gap: 1rem; margin-bottom: 1.15rem; align-items: flex-start; flex-wrap: wrap; }
      .header-actions { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
      .lead-filter { min-width: 240px; margin: 0; }
      h1 { margin: 0 0 0.4rem; font-size: clamp(1.55rem, 1.3rem + 0.8vw, 1.9rem); font-weight: 700; letter-spacing: -0.03em; }
      header p { margin: 0; color: var(--tt-muted); max-width: 36rem; line-height: 1.5; }
      .scope-note {
        display: flex; align-items: flex-start; gap: 0.5rem;
        background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe;
        border-radius: 8px; padding: 0.65rem 0.85rem; margin-bottom: 1rem; font-size: 0.9rem;
      }
      .scope-note mat-icon { font-size: 18px; width: 18px; height: 18px; margin-top: 2px; }
      .error { margin-bottom: 1rem; color: #b91c1c; }
      .cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem; }
      .cards mat-card { padding: 0.95rem 1rem; display: flex; flex-direction: column; gap: 0.25rem; }
      .cards strong { font-size: 1.45rem; letter-spacing: -0.03em; color: var(--tt-ink); }
      .cards span { color: var(--tt-muted); font-size: 0.78rem; font-weight: 500; }
      .cards .warn strong { color: var(--tt-danger); }
      .task { padding: 1rem 1.05rem; margin: 0.75rem 0; }
      .task h3 { margin: 0 0 0.35rem; font-size: 1rem; letter-spacing: -0.02em; }
      .meta { color: var(--tt-muted); font-size: 0.85rem; display: flex; align-items: center; gap: 0.25rem; }
      .meta .inline { font-size: 14px; width: 14px; height: 14px; }
      .row { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
      .empty { color: var(--tt-muted); padding: 1rem 0; }
      .actions { display: flex; flex-wrap: wrap; gap: 0.4rem; }
      .tab-badge { margin-left: 6px; background: #e2e8f0; padding: 0 6px; border-radius: 8px; font-size: 0.75rem; }
      .tab-badge.warn { background: #fef2f2; color: #b91c1c; }
      .task.overdue { border-left: 3px solid var(--tt-danger, #dc2626); }
      .due { display: flex; align-items: center; gap: 0.25rem; font-size: 0.85rem; margin: 0.25rem 0; }
      .due.overdue { color: #b91c1c; font-weight: 600; }
      .due mat-icon { font-size: 16px; width: 16px; height: 16px; }
    `
  ]
})
export class TeamLeadDashboardComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly data = signal<DashboardData | null>(null);
  readonly members = signal<Record<string, MemberOption[]>>({});
  readonly leads = signal<LeadOption[]>([]);
  readonly selectedLeadId = signal<string>('');
  readonly isPrivileged = signal(false);
  readonly scopeNote = signal<string | null>(null);
  assignMap: Record<string, string> = {};

  ngOnInit(): void {
    this.loadLeads();
    this.load();
  }

  loadLeads(): void {
    this.api.get<unknown>('v1/tasks/allocation/leads').subscribe({
      next: (res) => {
        const raw = Array.isArray(res) ? res : ((res as { items?: unknown[] })?.items ?? []);
        this.leads.set(raw.map((item) => {
          const x = item as Record<string, unknown>;
          return {
            leadEmployeeId: String(x['leadEmployeeId'] ?? x['LeadEmployeeId']),
            leadName: String(x['leadName'] ?? x['LeadName'] ?? 'Team lead'),
            teamName: String(x['teamName'] ?? x['TeamName'] ?? '')
          };
        }));
      },
      error: () => this.leads.set([])
    });
  }

  onLeadFilterChange(value: string): void {
    this.selectedLeadId.set(value ?? '');
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const params: Record<string, string> = {};
    const lead = this.selectedLeadId();
    if (lead) params['teamLeadId'] = lead;

    this.api.get<Record<string, unknown>>('v1/tasks/team-lead/dashboard', Object.keys(params).length ? params : undefined).subscribe({
      next: (raw) => {
        const cards = (raw['cards'] ?? raw['Cards'] ?? {}) as Record<string, number>;
        const mapTasks = (list: unknown): TaskRow[] =>
          (Array.isArray(list) ? list : []).map((item) => {
            const x = item as Record<string, unknown>;
            return {
              id: String(x['id'] ?? x['Id']),
              title: String(x['title'] ?? x['Title'] ?? ''),
              description: (x['description'] ?? x['Description']) as string | undefined,
              status: Number(x['status'] ?? x['Status'] ?? 0),
              statusLabel: (x['statusLabel'] ?? x['StatusLabel']) as string | undefined,
              teamLeadId: (x['teamLeadId'] ?? x['TeamLeadId']) as string | undefined,
              teamLeadName: (x['teamLeadName'] ?? x['TeamLeadName']) as string | undefined,
              dueDate: (x['dueDate'] ?? x['DueDate']) as string | undefined,
              isOverdue: Boolean(x['isOverdue'] ?? x['IsOverdue']),
              delayLabel: (x['delayLabel'] ?? x['DelayLabel']) as string | undefined,
              isDistributed: Boolean(x['isDistributed'] ?? x['IsDistributed']),
              allowedTransitions: (x['allowedTransitions'] ?? x['AllowedTransitions'] ?? []) as TaskRow['allowedTransitions']
            };
          });

        this.isPrivileged.set(Boolean(raw['isPrivilegedView'] ?? raw['IsPrivilegedView']));
        this.scopeNote.set((raw['scopeNote'] ?? raw['ScopeNote'] ?? null) as string | null);

        this.data.set({
          cards: {
            totalAssigned: cards['totalAssigned'] ?? cards['TotalAssigned'] ?? 0,
            pendingDistribution: cards['pendingDistribution'] ?? cards['PendingDistribution'] ?? 0,
            inProgress: cards['inProgress'] ?? cards['InProgress'] ?? 0,
            awaitingReview: cards['awaitingReview'] ?? cards['AwaitingReview'] ?? 0,
            completed: cards['completed'] ?? cards['Completed'] ?? 0,
            overdue: cards['overdue'] ?? cards['Overdue'] ?? 0
          },
          myAssignedTasks: mapTasks(raw['myAssignedTasks'] ?? raw['MyAssignedTasks']),
          teamTasks: mapTasks(raw['teamTasks'] ?? raw['TeamTasks']),
          pendingDistribution: mapTasks(raw['pendingDistribution'] ?? raw['PendingDistribution']),
          inProgress: mapTasks(raw['inProgress'] ?? raw['InProgress']),
          awaitingReview: mapTasks(raw['awaitingReview'] ?? raw['AwaitingReview']),
          completed: mapTasks(raw['completed'] ?? raw['Completed']),
          overdue: mapTasks(raw['overdue'] ?? raw['Overdue'])
        });
        this.loading.set(false);
        for (const t of this.data()!.pendingDistribution) {
          this.loadMembers(t.id);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load team lead dashboard.');
      }
    });
  }

  membersFor(taskId: string): MemberOption[] {
    return this.members()[taskId] ?? [];
  }

  loadMembers(taskId: string): void {
    this.api.get<unknown[]>(`v1/tasks/${taskId}/assignable-members`).subscribe({
      next: (items) => {
        const list = (Array.isArray(items) ? items : []).map((item) => {
          const x = item as Record<string, unknown>;
          return {
            employeeId: String(x['employeeId'] ?? x['EmployeeId']),
            name: String(x['name'] ?? x['Name'] ?? ''),
            jobTitle: (x['jobTitle'] ?? x['JobTitle']) as string | undefined
          };
        });
        this.members.update((m) => ({ ...m, [taskId]: list }));
      },
      error: () => this.members.update((m) => ({ ...m, [taskId]: [] }))
    });
  }

  distribute(task: TaskRow): void {
    const assigneeId = this.assignMap[task.id];
    if (!assigneeId) return;
    this.api
      .post(`v1/tasks/${task.id}/assign-team-member`, {
        assigneeId,
        comment: 'Distributed by team lead'
      })
      .subscribe({
        next: () => this.load(),
        error: (err) => this.error.set(err?.message || 'Distribution failed.')
      });
  }

  transition(task: TaskRow, toStatus: number): void {
    this.api
      .post(`v1/tasks/${task.id}/transition`, { toStatus, action: 'team_lead_review', comment: null })
      .subscribe({
        next: () => this.load(),
        error: (err) => this.error.set(err?.message || 'Status update failed.')
      });
  }
}
